import React, { useContext, useRef, useState, useEffect } from 'react';
import { View } from 'react-native';
import { useTheme } from '@rneui/themed';
import Constants from 'expo-constants';
import PropTypes from 'prop-types';
import ServiceBlock from './ServiceBlock';
import { Dropdown } from '../common/Dropdown';
import { ViewMore } from '../common/ViewMore';
import { default as AppConstants } from '../../common/Constants';
import AppContext, { AppActions } from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';
import ComponentTitle from '../common/ComponentTitle';

const ServiceContainer = ({ campus, nodeTypeAlias = 'ServiceContainer', numberOfServicesToDisplay, speaker, services, title, width }) => {
    const { layout } = useContext(LayoutContext);
    const { dispatch } = useContext(AppContext);
    const scrollToRef = useRef();
    const [pageNumber, setPageNumber] = useState(1);
    const [filter, setFilter] = useState(0);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterOpts, setFilterOpts] = useState([
        {
            key: 0,
            label: 'All',
            value: 0
        }
    ]);
    const { theme } = useTheme();
    const [collectedServices, setCollectedServices] = useState(null);

    const dedupeServices = array => {
        if (Array.isArray(array)) {
            return array.reduce((unique, object) => {
                if (unique.length === 0 || !unique.some(service => service.title === object.title)) {
                    unique.push(object);
                }

                return unique;
            }, []);
        }

        return null;
    };

    const filterServices = () => {
        if (filter === 0) {
            return collectedServices;
        }

        return collectedServices?.filter(service => {
            const serviceCampus = service?.campus;
            const serviceSpeaker = service?.speaker;
            const selectedFilter = filterOpts.find(f => f.value === filter);

            if (serviceCampus?.equals(selectedFilter?.label)) {
                return service;
            } else if (serviceSpeaker?.equals(selectedFilter?.label)) {
                return service;
            } else {
                return null;
            }
        });
    };

    useEffect(() => {
        const allServices = [];

        if (!services || services.length === 0) {
            return [];
        }

        if (campus?.length === 0 && speaker?.length === 0) {
            services.forEach(item => {
                if (item.haivisionPublicId) {
                    allServices.push(item);
                }
            });
        } else {
            if (campus?.length > 0 && speaker?.length > 0) {
                services.forEach(item => {
                    campus.forEach(camp => {
                        if (item.campus?.includes(camp)) {
                            speaker.forEach(speaker => {
                                if (/*item.speaker.includes(speaker) && */ item.haivisionPublicId) {
                                    allServices.push(item);
                                }
                            });
                        }
                    });
                });
            }

            if (campus?.length === 0 && speaker) {
                services.forEach(item => {
                    if (/*item.speaker?.equals(speaker) && */ item.haivisionPublicId) {
                        allServices.push(item);
                    }
                });
            }

            if (campus?.length > 0 && speaker?.length === 0) {
                services.forEach(item => {
                    campus.forEach(camp => {
                        if (item.haivisionPublicId) {
                            allServices.push(item);
                        }
                    });
                });
            }
        }

        const filterOptions = filterOpts;
        allServices.forEach(item => {
            if (!filterOptions.find(filterOpt => filterOpt?.label?.equals(item.campus))) {
                filterOptions.push({
                    key: filterOptions.length - 1 + 1,
                    label: item.campus,
                    value: filterOptions.length - 1 + 1
                });
            }

            if (!filterOptions.find(filterOpt => filterOpt?.label?.equals(item.speaker)) && item.speaker.length > 0) {
                filterOptions.push({
                    key: filterOptions.length - 1 + 1,
                    label: item.speaker,
                    value: filterOptions.length - 1 + 1
                });
            }
        });

        setFilterOpts(filterOptions);
        const dedupedServices = dedupeServices(allServices);

        setCollectedServices(
            dedupedServices.sort((a, b) => {
                const aDate = new Date(a.dateAndTime);
                const bDate = new Date(b.dateAndTime);

                if (aDate < bDate) {
                    return 1;
                }

                if (aDate > bDate) {
                    return -1;
                }

                return 0;
            })
        );
    }, [campus, filterOpts, services, speaker]);

    const filteredServices = filterServices();
    if (!filteredServices || filteredServices.length === 0) {
        return null;
    }
    const isMore = numberOfServicesToDisplay * pageNumber < filteredServices?.length;
    const serviceData = filteredServices.slice(0, numberOfServicesToDisplay * pageNumber);

    const filteredFilterOpts = [...new Set(filterOpts.filter(o => !!o.label))];

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            <View
                style={{
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: layout.styleGuide.spacer2,
                    marginHorizontal: layout.styleGuide.spacer2
                }}
                dataSet={{
                    spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})`,
                    spacerLeft: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})`,
                    spacerRight: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})`
                }}
            >
                {!!title && (
                    <View style={{ justifyContent: 'flex-start' }}>
                        <ComponentTitle title={title} />
                    </View>
                )}
                <View style={{ justifyContent: 'flex-end' }}>
                    <Dropdown
                        isOpen={filterOpen}
                        onSelect={val => setFilter(val || 0)} // not sure why all is returning undefined @jacksonkr
                        options={filteredFilterOpts}
                        placeholder={layout.device.isMobile ? 'Filter By:' : `Filter By: ${filteredFilterOpts[filter] && filteredFilterOpts[filter].label}`}
                        placeholderIcon={layout.device.isMobile ? 'filter' : undefined}
                        placeholderStyle={theme?.appComponents?.umbraco?.ServiceContainer?.placeholderStyle}
                        setOpen={() => setFilterOpen()}
                        setValue={() => setFilter()}
                        textColor='#153d6e'
                        value={filteredFilterOpts[filter]}
                        iconColor='#000'
                        style={{
                            width: '100%',
                            flexDirection: layout.device.isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}
                    />
                </View>
            </View>
            <View
                style={{ marginHorizontal: layout.styleGuide.spacer1, marginVertical: layout.styleGuide.spacer3 }}
                dataSet={{
                    spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`,
                    spacerLeft: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})`,
                    spacerRight: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})`,
                    spacerTop: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`
                }}
            >
                {serviceData.map(item => {
                    const watch = async () => {
                        const response = await window.fetch(
                            `${Constants.expoConfig.extra?.baseUrl}${AppConstants.REACT_APP_PAGE_BASE}${AppConstants.REACT_APP_INTERNAL_REDIRECT_FEATURED}`
                        );
                        const json = await response.json();

                        if (json) {
                            dispatch({
                                type: AppActions.WATCH_FEATURED_SERVICE,
                                data: {
                                    page: json.page,
                                    service: item
                                }
                            });
                        }
                    };

                    return (
                        <ServiceBlock
                            containerStyle={{ paddingVertical: 20 }}
                            date={item.viewDateTime}
                            details={item.fullDescription}
                            key={item.id}
                            sermonTitle={item.title}
                            speaker={item.speaker}
                            speakerImage={item.speakerPhoto.cdnImagePath}
                            onClick={() => {
                                if (item.haivisionPublicId?.length > 0) {
                                    watch();
                                }
                            }}
                            renderBorder={true}
                            width={width}
                        />
                    );
                })}
            </View>
            {filteredServices.length > numberOfServicesToDisplay && (
                <ViewMore
                    handler={() => {
                        setPageNumber(isMore ? pageNumber + 1 : pageNumber - 1);
                    }}
                    text={isMore ? 'more' : 'less'}
                />
            )}
            <View style={{ height: 1, width: 1 }} ref={scrollToRef} />
        </View>
    );
};

ServiceContainer.propTypes = {
    campus: PropTypes.arrayOf(PropTypes.string),
    numberOfServicesToDisplay: PropTypes.number,
    title: PropTypes.string,
    services: PropTypes.arrayOf(
        PropTypes.shape({
            briefDescription: PropTypes.string,
            buttonText: PropTypes.string,
            campus: PropTypes.string,
            featured: PropTypes.bool,
            fullDescription: PropTypes.string,
            haivisionPublicId: PropTypes.string,
            isGuest: PropTypes.bool,
            key: PropTypes.string.isRequired,
            speaker: PropTypes.string.isRequired,
            speakerPhoto: PropTypes.object,
            textColor: PropTypes.string,
            title: PropTypes.string.isRequired,
            dateAndTime: PropTypes.string
        })
    ),
    speaker: PropTypes.string
};

export default ServiceContainer;
